import { Button, Checkbox } from '@today/ui-primitives'
import type { ReactElement, SubmitEvent } from 'react'
import { ErrorText } from './product-fields'
import type { Translate } from './product-types'

/**
 * Props for {@link LocaleSettingsView}. The subset rules live in the page hook's
 * reducer; this view reports edits and renders the state.
 *
 * @property addError - Why the last add was refused (translated), or null.
 * @property formError - A blocking client problem or a server refusal (translated), or null.
 * @property unconfigured - True while the tenant has never saved settings and nothing is enabled.
 */
export interface LocaleSettingsViewProps {
	t: Translate
	enabledLocales: string[]
	requiredLocales: string[]
	defaultLocale: string | null
	unconfigured: boolean
	addError: string | null
	formError: string | null
	saved: boolean
	isBusy: boolean
	onAdd: (locale: string) => void
	onRemove: (locale: string) => void
	onToggleRequired: (locale: string) => void
	onSetDefault: (locale: string) => void
	onSave: () => void
}

function LocaleRow({ props, locale }: { props: LocaleSettingsViewProps; locale: string }): ReactElement {
	const { t } = props
	return (
		<li className="flex items-center gap-4">
			<code className="w-24 text-ds-base">{locale}</code>
			<Checkbox isSelected={props.requiredLocales.includes(locale)} onChange={() => props.onToggleRequired(locale)}>
				{`${t('localeSettings.required')} (${locale})`}
			</Checkbox>
			<label className="flex items-center gap-1 text-ds-sm">
				<input
					type="radio"
					name="defaultLocale"
					checked={props.defaultLocale === locale}
					onChange={() => props.onSetDefault(locale)}
				/>
				{`${t('localeSettings.default')} (${locale})`}
			</label>
			<Button variant="muted" size="sm" onPress={() => props.onRemove(locale)}>
				{`${t('localeSettings.remove')} ${locale}`}
			</Button>
		</li>
	)
}

/** Tenant locale settings: enabled list with required/default, add and save (organism). */
export function LocaleSettingsView(props: LocaleSettingsViewProps): ReactElement {
	const { t } = props
	const add = (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault()
		const form = event.currentTarget
		props.onAdd(String(new FormData(form).get('locale') ?? '').trim())
		form.reset()
	}
	return (
		<section className="flex flex-col gap-4">
			<h1 className="text-ds-xl font-semibold text-ds-text">{t('localeSettings.title')}</h1>
			{props.unconfigured ? <p role="note">{t('localeSettings.unconfigured')}</p> : null}
			<ul className="flex flex-col gap-2">
				{props.enabledLocales.map((locale) => (
					<LocaleRow key={locale} props={props} locale={locale} />
				))}
			</ul>
			<form onSubmit={add} className="flex items-end gap-2">
				<label className="flex flex-col gap-1.5 text-ds-sm text-ds-text-muted">
					{t('localeSettings.addLabel')}
					<input name="locale" className="h-10 rounded-ds-md border border-ds-border px-3" />
				</label>
				<Button type="submit">{t('localeSettings.add')}</Button>
			</form>
			<ErrorText message={props.addError} />
			<ErrorText message={props.formError} />
			{props.saved ? <p role="status">{t('localeSettings.saved')}</p> : null}
			<Button isDisabled={props.isBusy} onPress={props.onSave}>
				{t('localeSettings.save')}
			</Button>
		</section>
	)
}
